import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BaseDraftCard from '../commonUtils/baseDraftCard';
import './Activities.css';

const Activities = ({ onBack, fetchWorkOrders }) => {
    const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(1); // 默认选中"营销活动"
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState('Operation'); // 操作类型默认运营管理
  const [selectedStatus, setSelectedStatus] = useState(''); // 状态默认不选

  // 操作类型选项
  const eventTypeOptions = [
    { label: '运营管理', value: 'Operation' },
    { label: '营销', value: 'Promotion' },
    { label: '物流', value: 'Logistics' }
  ];

  // 状态选项
  const statusOptions = [
    { label: '进行中', value: 'Progressing' },
    { label: '待处理', value: 'Pending' },
    { label: '已完成', value: 'Completed' }
  ];

  const tabs = [
    { id: 0, label: t('activities.workOrders', '工单'), emptyTitle: t('activities.noWorkOrders', '暂无工单'), emptyDesc: t('activities.generateWorkOrders', '通过AI助手生成工单') },
    { id: 1, label: t('activities.marketing', '营销活动'), emptyTitle: t('activities.noMarketing', '暂无营销活动'), emptyDesc: t('activities.generateMarketing', '通过AI助手生成智能营销活动计划') },
    { id: 2, label: t('activities.bubble', '12POP泡泡'), emptyTitle: t('activities.noBubble', '暂无泡泡内容'), emptyDesc: t('activities.generateBubble', '通过AI助手生成12POP泡泡内容') },
    { id: 3, label: t('activities.records', '记录'), emptyTitle: t('activities.noRecords', '暂无活动记录'), emptyDesc: t('activities.viewRecords', '查看您的活动历史和记录') }
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  // 获取工单数据
  const loadWorkOrders = async (eventType = selectedEventType, status = selectedStatus) => {
    if (!fetchWorkOrders) return;
    
    setLoading(true);
    try {
      const result = await fetchWorkOrders(eventType, status);
      if (result && result.data) {
        setWorkOrders(result.data);
      }
    } catch (error) {
      console.error('Failed to load work orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时，如果当前是工单标签页，则加载数据
  useEffect(() => {
    if (activeTab === 0 && fetchWorkOrders) {
      loadWorkOrders();
    }
  }, [activeTab, fetchWorkOrders]);

  // 刷新工单数据
  const refreshWorkOrders = () => {
    loadWorkOrders(selectedEventType, selectedStatus);
  };

  // 处理操作类型选择变化
  const handleEventTypeChange = (value) => {
    setSelectedEventType(value);
    loadWorkOrders(value, selectedStatus);
  };

  // 处理状态选择变化
  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    loadWorkOrders(selectedEventType, value);
  };

  const currentTab = tabs.find(tab => tab.id === activeTab) || tabs[1];

  return (
    <div className="activities-container">
      {/* Header */}
      <div className="activities-header">
        <button 
          className="activities-back-btn" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onBack) {
              onBack();
            }
          }}
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <h1 className="activities-title">{t('activities.title', '活动')}</h1>
      </div>

      {/* Tabs */}
      <div className="activities-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`activities-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabClick(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="activities-content">
        {activeTab === 0 ? (
          // 工单标签页内容
          <>
            {/* 工单列表标题和刷新按钮 */}
            <div className="work-orders-header">
              <h3>{t('activities.workOrdersList', '工单列表')}</h3>
              <button 
                className="refresh-btn" 
                onClick={refreshWorkOrders}
                title={t('activities.refresh', '刷新')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                </svg>
                {t('activities.refresh', '刷新')}
              </button>
            </div>

            {/* 操作类型和状态选择器 */}
            <div className="work-orders-filters">
              <div className="filter-group">
                <label className="filter-label">操作类型：</label>
                <div className="filter-options">
                  {eventTypeOptions.map(option => (
                    <button
                      key={option.value}
                      className={`filter-option ${selectedEventType === option.value ? 'active' : ''}`}
                      onClick={() => handleEventTypeChange(selectedEventType === option.value ? '' : option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter-group">
                <label className="filter-label">状态：</label>
                <div className="filter-options">
                  {statusOptions.map(option => (
                    <button
                      key={option.value}
                      className={`filter-option ${selectedStatus === option.value ? 'active' : ''}`}
                      onClick={() => handleStatusChange(selectedStatus === option.value ? '' : option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {loading ? (
              <div className="activities-loading">
                <div className="loading-spinner"></div>
                <p>{t('activities.loading', '加载中...')}</p>
              </div>
            ) : workOrders.length > 0 ? (
              <div className="work-orders-list">
                {workOrders.map((order, index) => (
                  <BaseDraftCard
                    key={index}
                    cardType={"ServiceTicket"}
                    title={order.title}
                    text={order.content}
                    language={i18n.language === 'zh' ? 'zh_cn' : 'en_us'}
                    editable={false}
                    serviceTicketExtraInfo={{
                      status: getOrderStatus(order.status),
                      statusText: getOrderStatusText(order.status),
                      level: getOrderLevel(order.priority),
                      levelText: getOrderLevelText(order.priority),
                      trackingNumber: {
                        text: `${t('activities.workOrderId', '工单号')}: ${order.id}`
                      }
                    }}
                    bottomInfo={[
                      {
                        icon: "servicer-o",
                        text: order.servicer
                      },
                      {
                        icon: "alarm",
                        text: order.createTime
                      }
                    ]}
                  />
                ))}
              </div>
            ) : (
              <div className="activities-empty-state">
                <div className="activities-empty-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 2h14v16H4V2z" fill="url(#flagGradient)"/>
                    <path d="M4 2l7 4L18 2" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    <path d="M4 10l7 4L18 10" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    <defs>
                      <linearGradient id="flagGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="1"/>
                        <stop offset="30%" stopColor="#6366f1" stopOpacity="1"/>
                        <stop offset="60%" stopColor="#8b5cf6" stopOpacity="1"/>
                        <stop offset="100%" stopColor="#a855f7" stopOpacity="1"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <h2 className="activities-empty-title">{currentTab.emptyTitle}</h2>
                <p className="activities-empty-description">
                  {currentTab.emptyDesc}
                </p>
              </div>
            )}
          </>
        ) : (
          // 其他标签页的空状态
          <div className="activities-empty-state">
            <div className="activities-empty-icon">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 2h14v16H4V2z" fill="url(#flagGradient)"/>
                <path d="M4 2l7 4L18 2" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <path d="M4 10l7 4L18 10" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <defs>
                  <linearGradient id="flagGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="1"/>
                    <stop offset="30%" stopColor="#6366f1" stopOpacity="1"/>
                    <stop offset="60%" stopColor="#8b5cf6" stopOpacity="1"/>
                    <stop offset="100%" stopColor="#a855f7" stopOpacity="1"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h2 className="activities-empty-title">{currentTab.emptyTitle}</h2>
            <p className="activities-empty-description">
              {currentTab.emptyDesc}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // 获取工单状态文本
  function getOrderStatusText(status) {
    switch(status) {
      case 0: return t('activities.statusPending', '待处理');
      case 1: return t('activities.statusProcessing', '进行中');
      case 2: return t('activities.statusCompleted', '已完成');
      default: return t('activities.statusUnknown', '未知');
    }
  }

  // 获取工单状态类型
  function getOrderStatus(status) {
    switch(status) {
      case 0: return 'pend';
      case 1: return 'progress';
      case 2: return 'finish';
      default: return 'draft';
    }
  }

  // 获取工单优先级文本
  function getOrderLevelText(priority) {
    switch(priority) {
      case 'high': return t('activities.priorityHigh', '高');
      case 'medium': return t('activities.priorityMedium', '中');
      case 'low': return t('activities.priorityLow', '低');
      default: return t('activities.priorityMedium', '中');
    }
  }

  // 获取工单优先级类型
  function getOrderLevel(priority) {
    switch(priority) {
      case 'high': return 'high';
      case 'medium': return 'middle';
      case 'low': return 'low';
      default: return 'low';
    }
  }
};

export default Activities;

